import { useEffect, useState, useCallback, useRef } from 'react';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BackLink from '../components/shared/BackLink';
import { formatDay } from '../i18n';

// Stage labels resolved inside component via t.common.*

// -- Bout time formula (IFMA 2025/2026) --
import { HEAD_STRIKE_RULE, deriveAgeGroup, deriveWeightClass, roundFormula } from '../utils/tournament';
import { boutSeatsOf } from '../utils/boutSeats';
import { getBout, getBoutSubscription, updateBout, deleteBout } from '../api/client';
import { useAuthStore } from '../store/auth';
import { useI18n } from '../i18n';
import { winnerLabel } from '../i18n/labels';
import { apiError, formatWarnings } from '../hooks/useApiCall';
import { useSocket, useRooms, bindSocket, rejoinBoutOfficialsRoom } from '../hooks/useSocket';
import { usePageTitle } from '../hooks/usePageTitle';
import { useDeferredLoading } from '../hooks/useDeferredLoading';
import { useBusy, useBusyKeys } from '../hooks/useBusy';
import BoutCard from '../components/bout/BoutCard';
import VerdictResult from '../components/bout/VerdictResult';
import { useEditBout } from '../components/tournament/EditBoutModal';
import BoutFormModal from '../components/tournament/BoutFormModal';
import RoundClock from '../components/bout/RoundClock';
import RoundScoring from '../components/bout/RoundScoring';
import LoadFailure from '../components/shared/LoadFailure';
import GenderBadge from '../components/shared/GenderBadge';
import BoutCorners from '../components/bout/BoutCorners';
import BoutOfficials from '../components/bout/BoutOfficials';
import BoutLevelPill from '../components/bout/BoutLevelPill';
import BoutStatusPill from '../components/bout/BoutStatusPill';
import StagePill from '../components/bout/StagePill';
import { BellIcon, PencilIcon, ClockIcon, CalendarIcon, PlayIcon, ChevronRightIcon, GloveIcon } from '../components/shared/Icons';
import GuestBellPrompt from '../components/bout/GuestBellPrompt';
import { followBout, unfollowBout } from '../lib/boutBell';
import { logBackgroundError } from '../observability';
import { useOnResume } from '../lib/resume';
import type {
  Bout,
  SocketBoutStatusChanged, SocketBoutUpdated, SocketBoutRefereeJoined, SocketBoutRefereeAssigned,
  SocketBoutOfficialsChanged, SocketBoutVerdict, SocketBoutAdvanced,
} from '../types';

/** The id in the URL can have capital letters; the API gives lower case. */
const sameBoutId = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** #664 (F49) — the roles that join `bout:<id>:officials` on this page. The
 *  server runs its own check on each join. The same list gates the rejoin
 *  after a roster event. */
const OFFICIALS_ROOM_ROLES = ['referee', 'jury', 'admin', 'tournament_manager'] as const;

export default function BoutDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole, user } = useAuthStore();
  const { t } = useI18n();
  const socket = useSocket();

  const [bout, setBout] = useState<Bout | null>(null);
  const [loading, setLoading] = useState(true);
  // #458 — the loading text shows after 200 ms and holds 400 ms (useDeferredLoading):
  // a fast load shows nothing, not a one-frame flash.
  const showLoading = useDeferredLoading(loading);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  usePageTitle([bout && `${t.common.bout} #${bout.bout_number}`, bout?.tournament_name]);
  const [nextBoutCountdown, setNextBoutCountdown] = useState<{ boutId: string; seconds: number } | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  // #593 — the bell shows the new state at once; a tap during the request does nothing.
  const [bellBusy, setBellBusy] = useState(false);
  const bellBusyRef = useRef(false);
  /** The count of bell taps. The answer of the first read of the bell applies
   *  only when no tap came after the read started: a late answer overwrote a
   *  follow that the server held (`bout-bell-guest-515`). */
  const bellTaps = useRef(0);
  const [showGuestBell, setShowGuestBell] = useState(false); // #515
  // #605 — the lock of Start bout: a second tap sends no second PATCH.
  const starting = useBusy();
  // #605 — a lock for each bout. The edit modal closes at once, and this page
  // can show another bout before the answer: a delete of that bout must not wait.
  const deletes = useBusyKeys();

  // Toast — through the provider (2026-09-19). This page kept its own
  // `{ msg, type }` state and rendered <Toast message> without the kind, so
  // every colour came from English words in the message ("Error", "failed"),
  // which a Lithuanian message defeats, and its error paths were never red.
  const { showToast, showError } = useToast();
  const { confirm } = useConfirm();

  const { editData, openEditBout, closeEditBout } = useEditBout();

  const canOfficiate = hasRole('referee', 'jury', 'admin');

  // #664 (F49) — the rooms of this bout while the page shows it. The officials
  // room is for the roles that the server gate can accept; the gate itself
  // reads the database. (BoutCard owns the same room for the score cards, with
  // its own gate.)
  useRooms([
    id ? `bout:${id}` : null,
    id && hasRole(...OFFICIALS_ROOM_ROLES) ? `bout:${id}:officials` : null,
  ]);

  const reloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The bout of the page now. `/bouts/:id` reuses this page for the next
   *  bout, so a reload that started for the bout before must drop its answer. */
  const currentId = useRef(id);
  currentId.current = id;
  /** The socket writes of the page: a judge joins, a ring referee, a jury, a
   *  verdict. If a write arrives during a load, the answer of that load is
   *  older, so the page loads the bout again. Without this check, the jury
   *  page stayed at "Side Judges (2/5)" after the third join (#622 review). */
  const socketWrites = useRef(0);
  /** One read of the bout. It rejects when the read fails. It answers `false`
   *  when a socket write came during the read: that answer is older, the page
   *  drops it, and the caller reads again. */
  const readBout = useCallback(async (): Promise<boolean> => {
    if (!id) return true;
    const writesAtStart = socketWrites.current;
    const res = await getBout(id);
    if (currentId.current !== id) return true;
    if (socketWrites.current !== writesAtStart) return false;
    setBout(res.data);
    return true;
  }, [id]);
  /** The reload of the socket events and of the resume: debounced, and it never
   *  rejects. A failed refresh keeps the bout on screen; the next event loads again. */
  const reload = useCallback(async () => {
    if (!id) return;
    if (reloadRef.current) clearTimeout(reloadRef.current);
    reloadRef.current = setTimeout(async () => {
      try {
        if (!(await readBout())) reload();
      } catch (err) { logBackgroundError('BoutDetail:reload')(err); }
    }, 300);
  }, [id, readBout]);
  /** #664 — the read that a seat action of BoutOfficials awaits. It has no
   *  debounce and it rejects when the read fails, so the lock of the seat ends
   *  after the new state shows. A socket write during the read makes one more
   *  read; after that the debounced reload takes over. */
  const refreshBout = useCallback(async () => {
    if (await readBout() || await readBout()) return;
    await reload();
  }, [readBout, reload]);
  // #499 — reload after a gap in the socket.
  useOnResume(reload, 'bout');

  useEffect(() => {
    if (!id) return;
    setBout(null); setLoading(true); setLoadError(null);
    // #457 — a failed load left "Loading…" on screen for ever.
    const writesAtLoad = socketWrites.current;
    getBout(id)
      .then(r => {
        if (currentId.current !== id) return;
        setBout(r.data);
        // A socket write during the first load found no bout to patch: load again.
        if (socketWrites.current !== writesAtLoad) reload();
      })
      .catch(err => { logBackgroundError('BoutDetail:load')(err); if (currentId.current === id) setLoadError(err); })
      .finally(() => { if (currentId.current === id) setLoading(false); });
    // Check subscription status
    if (user) {
      const taps = bellTaps.current;
      getBoutSubscription(id)
        .then(r => { if (currentId.current === id && bellTaps.current === taps) setSubscribed(r.data.subscribed); })
        .catch(logBackgroundError('BoutDetail:subscription'));
    }

    // Targeted socket handlers — update specific fields instead of full reload
    // The texts come from the store, not the hook: a handler outlives a
    // language change (#458).
    const tr = () => useI18n.getState().t;
    const onPublicScores = () => reload();
    const onDisplayRounds = () => reload();
    const onStatusChanged = ({ boutId: changedId }: SocketBoutStatusChanged) => { if (changedId === id) reload(); };
    // #459 — an edit without a status change (corners, ring, date).
    const onUpdated = ({ boutId: changedId }: SocketBoutUpdated) => { if (changedId === id) reload(); };
    // When officials roster changes, rejoin the officials room. The backend's
    // `join:bout:officials` handler gates membership on current DB state —
    // a jury/referee who JUST claimed the bout was denied entry on mount
    // (no bout_jury / bout_referees row existed yet), and without re-
    // emitting they'd never receive `score:submitted` events for the rest
    // of the bout's lifetime. Re-joining on every officials change is safe:
    // socket.io dedupes on the server side, and the backend re-runs the
    // permission check so unauthorised re-joins are still rejected.
    const rejoinOfficials = () => {
      if (id && hasRole(...OFFICIALS_ROOM_ROLES)) rejoinBoutOfficialsRoom(id);
    };
    const onRefereeJoined = (data: SocketBoutRefereeJoined) => {
      socketWrites.current++;
      if (data?.referees) setBout(prev => prev ? { ...prev, referees: data.referees } : prev);
      showToast(tr().bout.sideJudgesUpdated.replace('{n}', String(data?.referees?.length ?? 0)), 'success');
      rejoinOfficials();
    };
    const onRefereeAssigned = (data: SocketBoutRefereeAssigned) => {
      socketWrites.current++;
      setBout(prev => prev ? { ...prev, ring_referee_id: data.ring_referee_id ?? undefined, ring_ref_first: data.ring_ref_first, ring_ref_last: data.ring_ref_last } : prev);
      // #589 — a ring referee who left the referee pool leaves the seat empty.
      showToast(data.ring_referee_id ? tr().bout.ringRefereeAssigned : tr().bout.ringRefereeRemoved, 'success');
      rejoinOfficials();
    };
    const onOfficialsChanged = (data: SocketBoutOfficialsChanged) => {
      socketWrites.current++;
      setBout(prev => prev ? {
        ...prev, jury_id: data.jury_id ?? undefined, jury_first: data.jury_first ?? undefined, jury_last: data.jury_last ?? undefined,
      } : prev);
      showToast(data.jury_id ? tr().bout.juryClaimedLive : tr().bout.juryUnclaimed, 'success');
      rejoinOfficials();
    };
    const onVerdict = (data: SocketBoutVerdict) => {
      socketWrites.current++;
      setBout(prev => prev ? { ...prev, verdict: data?.verdict, status: 'scored' } : prev);
      if (data?.verdict) showToast(tr().bout.verdictToast
        .replace('{winner}', winnerLabel(tr(), data.verdict.winner))
        .replace('{outcome}', data.verdict.outcome), 'success');
    };
    const onAdvanced = ({ currentBout }: SocketBoutAdvanced) => {
      reload();
      if (currentBout?.id) setNextBoutCountdown({ boutId: currentBout.id, seconds: 8 });
    };

    // The socket is null on the first run, and bindSocket then binds nothing.
    const unbind = bindSocket(socket, {
      'bout:public_scores_updated': onPublicScores,
      'bout:display_rounds_updated': onDisplayRounds,
      'bout:status_changed': onStatusChanged,
      'bout:updated': onUpdated,
      'bout:referee_joined': onRefereeJoined,
      'bout:referee_assigned': onRefereeAssigned,
      'bout:officials_changed': onOfficialsChanged,
      'bout:verdict': onVerdict,
      'bout:advanced': onAdvanced,
    });

    return () => {
      // A reload that waits for its 300 ms must not run for the page after this one.
      if (reloadRef.current) { clearTimeout(reloadRef.current); reloadRef.current = null; }
      unbind();
    };
  }, [id, socket, reload, loadAttempt]);

  // Each state that this page holds for ONE bout starts again when the bout
  // changes. `/bouts/:id` reuses this page, so "Go now" or any other link to
  // a bout kept the countdown card and its timer on the next page: the card
  // stayed for seconds, and the timer could move a person from another bout
  // (operator report, 2026-10-11). The bell and its sign-in dialog were the
  // same kind of state. The cards below the header remount: the page shows
  // its loading state between two bouts.
  useEffect(() => {
    setNextBoutCountdown(null);
    setSubscribed(false);
    setBellBusy(false);
    bellBusyRef.current = false;
    setShowGuestBell(false);
  }, [id]);

  // -- Countdown to next bout after verdict --
  useEffect(() => {
    if (!nextBoutCountdown) return;
    if (nextBoutCountdown.seconds <= 0) {
      navigate(`/bouts/${nextBoutCountdown.boutId}`);
      setNextBoutCountdown(null);
      return;
    }
    const timer = setTimeout(() => {
      setNextBoutCountdown(prev => prev ? { ...prev, seconds: prev.seconds - 1 } : null);
    }, 1000);
    return () => clearTimeout(timer);
  }, [nextBoutCountdown, navigate]);

  if (showLoading) return <div className="card p-8 text-center text-gray-400">{t.common.loading}</div>;
  if (loading) return null;
  // The first render after a change of the URL still holds the bout before:
  // draw nothing of it under the new URL.
  if (bout && id && !sameBoutId(bout.id, id)) return null;
  if (!bout) {
    // The tournament of a missing bout is unknown, so the link goes to the list of tournaments.
    return <LoadFailure error={loadError} onRetry={() => setLoadAttempt(n => n + 1)}
      notFound={{ icon: <GloveIcon className="w-12 h-12" />, title: t.common.boutNotFound, to: '/tournaments', linkLabel: t.common.allTournaments }} />;
  }

  // -- Derived state --
  const hasVerdict        = !!bout.verdict;
  const seats             = boutSeatsOf(bout, user?.id);
  // #454 — a live bout only: the server refuses a card for any other status
  // (SCORES_LIVE_ONLY), and the form showed on a pending bout.
  const canScore          = canOfficiate && seats.sideJudge && !hasVerdict && bout.status === 'current';

  return (
    <div className="space-y-4 max-w-2xl mx-auto">

      {/* Toast */}

      {/* -- Back navigation (#595): to the Bouts tab of the tournament, at this bout -- */}
      <div className="flex items-center gap-3">
        {bout.tournament_id && (
          <BackLink to={`/tournaments/${bout.tournament_id}?tab=bouts#bout-${bout.id}`}
            label={bout.tournament_name || t.common.back} />
        )}
      </div>

      {/* -- 2. CONTENDER CARDS -- */}
      <BoutCorners bout={bout} />

      {/* -- RESULT (scored only) -- */}
      {bout.status === 'scored' && bout.verdict && <VerdictResult bout={bout} verdict={bout.verdict} />}

      {/* -- Next bout countdown -- */}
      {nextBoutCountdown && (
        <div className="card p-4 bg-green-50 border-green-300 flex items-center justify-between">
          <div>
            <div className="font-bold text-green-800">{t.bout.nextBoutStarting}</div>
            <div className="text-sm text-green-600">{t.bout.redirectingIn.replace('{n}', String(nextBoutCountdown.seconds))}</div>
          </div>
          <div className="flex gap-2">
            <Link to={`/bouts/${nextBoutCountdown.boutId}`} className="btn-primary btn-sm">{t.bout.goNow}<ChevronRightIcon className="w-4 h-4" /></Link>
            <button onClick={() => setNextBoutCountdown(null)} className="btn-outline btn-sm">{t.bout.stayHere}</button>
          </div>
        </div>
      )}

      {/* -- 1. BOUT INFO TILE -- */}
      <div className="card px-4 py-3 space-y-3">

        {/* number / stage / ring / status */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
          <span className="font-black text-gray-900 text-base">{t.common.bout} {bout.bout_number}</span>
          {bout.manages_tournament && bout.status === 'pending' && (
            <button onClick={() => openEditBout(bout).catch(err => showError(err))} data-testid="bout-page-edit"
              className="btn-icon" title={t.bout.editBout} aria-label={t.bout.editBout}>
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          <StagePill stage={bout.stage} />
          {bout.ring_name && (
            <span className="text-gray-600">{t.common.ring}: <strong>{bout.ring_name}</strong></span>
          )}
          <BoutStatusPill status={bout.status} />
          {bout.manages_tournament && bout.status === 'pending' && (
            bout.ring_live_bout_number != null ? (
              <span className="badge badge-amber ml-auto" title={t.tournament.ringBusyBoutLive.replace('{n}', String(bout.ring_live_bout_number))}>
                {t.tournament.ringBusyBoutLive.replace('{n}', String(bout.ring_live_bout_number))}
              </span>
            ) : (
              <button
                // #665 — ask BEFORE the lock. The button is disabled while the lock is on.
                onClick={async () => {
                  if (starting.busy) return;
                  const ask = bout.ring_name
                    ? t.tournament.startBoutConfirm.replace('{n}', String(bout.bout_number)).replace('{ring}', bout.ring_name)
                    : t.tournament.startBoutConfirmNoRing.replace('{n}', String(bout.bout_number));
                  if (!(await confirm({ message: ask, confirmLabel: t.tournament.startBout }))) return;
                  await starting.run(async () => {
                    try {
                      const res = await updateBout(bout.id, { status: 'current' });
                      reload();
                      const advice = formatWarnings(res.data?.warnings);
                      if (advice) showToast(advice, 'warning');
                    } catch (err) { showError(err); }
                  });
                }}
                disabled={starting.busy}
                className="btn-sm ml-auto bg-green-600 text-white hover:bg-green-700 rounded-sm px-2.5 py-1 font-semibold inline-flex items-center gap-1">
                <PlayIcon className="w-3 h-3" />
                {t.tournament.startBout}</button>
            )
          )}
          {/* Notify me — every visitor sees it on a pending bout; a guest gets the sign-in prompt (#515) */}
          {bout.status === 'pending' && (
            <button
              onClick={async () => {
                // #515 — a guest gets the sign-in prompt, not a jump to the manual.
                if (!user) { setShowGuestBell(true); return; }
                // #593 — the new state at once; a second tap waits for the answer.
                if (bellBusyRef.current) return;
                const boutId = bout.id;
                const next = !subscribed;
                bellTaps.current++;
                bellBusyRef.current = true;
                setBellBusy(true);
                setSubscribed(next);
                // A result changes the page only while it shows the same bout.
                const same = () => sameBoutId(currentId.current, boutId);
                try {
                  if (next) await followBout(boutId);
                  else await unfollowBout(boutId);
                  if (same()) setSubscribed(next);
                } catch (err) {
                  if (same()) setSubscribed(!next);
                  showError(err);
                } finally {
                  // A read of the bell that started during this tap can hold the
                  // state before it: the count makes the read drop its answer.
                  if (same()) { bellTaps.current++; bellBusyRef.current = false; setBellBusy(false); }
                }
              }}
              data-testid="bout-bell"
              aria-busy={bellBusy}
              className={`btn-sm ml-auto rounded px-2.5 py-1 font-semibold inline-flex items-center gap-1 transition-colors ${
                subscribed
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              <BellIcon className="w-3.5 h-3.5" filled={subscribed} />
              {subscribed ? t.common.notifyOn : t.common.notifyMe}
            </button>
          )}
        </div>

        {/* Date */}
        {bout.bout_date && (
          <div className="text-xs text-gray-600 border-t border-gray-100 pt-2 flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" /> <span className="font-medium">{formatDay(bout.bout_date, 'EEEE, dd MMMM yyyy')}</span>
          </div>
        )}

        {/* Age group / weight class / bout time formula */}
        {(() => {
          // Explicit bout-level override takes priority; fall back to derived from contenders
          const ag  = bout.bout_age_group    || deriveAgeGroup(bout.red_age_group,    bout.blue_age_group);
          const wc  = bout.bout_weight_class || deriveWeightClass(bout.red_weight_class, bout.blue_weight_class);
          const tFormula = roundFormula(ag, bout.rounds); // #602: 3 or 5 rounds
          const strikeRule = ag ? HEAD_STRIKE_RULE[ag] : undefined; // #592
          if (!ag && !wc && !tFormula) return null;
          return (
            <div className="border-t border-gray-100 pt-2 flex flex-wrap gap-1.5">
              {(bout.red_gender || bout.blue_gender) && (() => {
                const g = (bout.red_gender || bout.blue_gender || '').toLowerCase();
                return <GenderBadge gender={g} division size="sm" className="gap-1" />;
              })()}
              {ag && <span className="badge badge-gray gap-1"><span className="opacity-50 font-normal">{t.bout.ageGroup}</span><span>{ag}</span></span>}
              {wc && <span className="badge badge-gray gap-1"><span className="opacity-50 font-normal">{t.bout.weightClass}</span><span>{wc}</span></span>}
              {tFormula && <span className="badge badge-gray gap-1 inline-flex items-center"><ClockIcon className="w-3 h-3 opacity-50" /><span>{tFormula}</span></span>}
              {strikeRule && <span className="badge badge-amber" data-testid="bout-strike-rule">{t.bout[strikeRule]}</span>}
              {/* #622 — the level of the bout, sent only to the roles of the Officials menu. */}
              {bout.level && <BoutLevelPill level={bout.level} estimated={bout.experience_estimated} />}
              {bout.level && bout.experience_estimated && <p className="basis-full text-xs text-gray-500">{t.bout.levelEstimated}</p>}
            </div>
          );
        })()}

        {/* Officials: ring referee / jury / side judges */}
        <BoutOfficials bout={bout} refresh={refreshBout} />
      </div>

      {/* -- 3. ROUND SCORING (assigned side judges only, before verdict) -- */}
      {canScore && <RoundScoring bout={bout} />}

      {/* -- 3.5 SARAMA PLAYER (jury only, current bout) -- */}
      {/* #600 — the Sarama and the backup timer of the jury. The key gives each
          bout its own timer: on the next bout the card closes, and its music
          stops. A key of its own: BoutCard beside it has key={bout.id}, and two
          siblings with one key made React draw the card twice. */}
      {seats.jury && bout.status === 'current' && (
        <RoundClock key={`clock-${bout.id}`} boutId={bout.id}
          ageGroup={bout.bout_age_group || deriveAgeGroup(bout.red_age_group, bout.blue_age_group)} />
      )}

      {/* -- 4. BOUT CARD (jury table + public scoreboard + verdict form) -- */}
      <BoutCard
        key={bout.id}
        bout={bout}
        onVerdictSubmitted={reload}
        isAssignedToBout={seats.sideJudge || seats.ringReferee}
      />

      {/* Edit bout modal */}
      {editData && (
        <BoutFormModal
          mode="edit"
          bout={editData.bout}
          tournamentId={editData.bout.tournament_id}
          tournamentType={editData.tournamentType}
          rings={editData.rings}
          referees={editData.referees}
          juryPool={editData.referees.filter((r: { in_jury_pool?: boolean }) => r.in_jury_pool)}
          registrations={editData.registrations}
          bouts={editData.bouts}
          tournamentDays={editData.tournamentDays}
          isMultiDay={editData.tournamentDays.length > 1}
          ageGroups={editData.ageGroups}
          onDone={async (warnings) => {
            closeEditBout(); reload();
            const advice = formatWarnings(warnings);
            if (advice) showToast(advice, 'warning');
            else showToast(t.bout.updated, 'success');
          }}
          onClose={closeEditBout}
          onDelete={(boutId) => deletes.run(boutId, async () => {
            try {
              await deleteBout(boutId);
              closeEditBout();
              navigate(`/tournaments/${bout.tournament_id}?tab=bouts`);
            } catch (err) { showError(err); }
          })}
        />
      )}
      {showGuestBell && <GuestBellPrompt onClose={() => setShowGuestBell(false)} />}
    </div>
  );
}
